import type { ReactNode } from 'react';
import React, { forwardRef } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import type { TabOrientation } from '../nativeTabsTypes.ts';

import { getTabColorAppearance, type TabColorKey } from './tabColorDefinitions.ts';
import { isStandardTabColorPaletteEnabled } from './tabColorFeatureGate.ts';

type TabPillColorSurfaceProps = {
	children: ReactNode;
	color?: TabColorKey;
	id: string;
	isActive: boolean;
	isDeletePreviewed: boolean;
	isEditing: boolean;
	onClick: (event: React.SyntheticEvent<HTMLElement>) => void;
	onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
	orientation?: TabOrientation;
	showMenu: boolean;
	tabIndex: number;
};

// The style prop assigns runtime values to these variables; cssMap consumes them below.
type TabPillColorCustomProperties = React.CSSProperties & {
	'--tab-pill-background-color'?: string;
	'--tab-pill-background-color-hovered'?: string;
	'--tab-pill-border-color'?: string;
	'--tab-pill-text-color'?: string;
};

const styles = cssMap({
	root: {
		display: 'flex',
		font: token('font.body'),
		alignItems: 'center',
		justifyContent: 'center',
		height: '32px',
		paddingInline: token('space.150'),
		borderRadius: token('radius.full'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		overflow: 'visible',
		userSelect: 'none',
		transition: 'background .1s ease-out',
		'&:hover': {
			cursor: 'pointer',
		},
		'&:focus-visible': {
			outlineColor: token('color.border.focused'),
			outlineStyle: 'solid',
			outlineWidth: token('border.width.focused'),
			outlineOffset: token('space.025'),
		},
	},
	vertical: {
		// Vertical tab titles wrap instead of truncating, so the pill grows with its title
		// while keeping the single-line height as its minimum.
		height: 'auto',
		minHeight: '32px',
		maxWidth: '248px',
		minWidth: 0,
		boxSizing: 'border-box',
		paddingBlock: token('space.050'),
		justifyContent: 'flex-start',
	},
	// A full radius would keep growing with each wrapped line. 16px matches the single-line
	// pill (half of 32px) and keeps corners consistent as titles wrap.
	verticalRadiusStart: {
		borderStartStartRadius: token('radius.xxlarge'),
		borderEndStartRadius: token('radius.xxlarge'),
	},
	verticalRadiusEnd: {
		borderStartEndRadius: token('radius.xxlarge'),
		borderEndEndRadius: token('radius.xxlarge'),
	},
	activeWithMenu: {
		borderStartEndRadius: 0,
		borderEndEndRadius: 0,
		borderInlineEndWidth: 0,
		paddingInlineEnd: token('space.075'),
	},
	deletePreview: {
		backgroundColor: token('color.background.danger'),
		color: token('color.text.danger'),
		borderColor: token('color.border.danger'),
	},
	defaultActive: {
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
		borderColor: token('color.border.selected'),
		borderWidth: token('border.width.focused'),
	},
	defaultInactive: {
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.subtle'),
		borderColor: token('color.border'),
		'&:hover': { backgroundColor: token('color.background.neutral.subtle.hovered') },
	},
	coloredActive: {
		// @ts-expect-error -- cssMap does not support runtime CSS variables for colors.
		backgroundColor: 'var(--tab-pill-background-color)',
		// @ts-expect-error -- cssMap does not support runtime CSS variables for colors.
		color: 'var(--tab-pill-text-color)',
		// @ts-expect-error -- cssMap does not support runtime CSS variables for colors.
		borderColor: 'var(--tab-pill-border-color)',
		borderWidth: token('border.width.focused'),
	},
	coloredInactive: {
		// @ts-expect-error -- cssMap does not support runtime CSS variables for colors.
		backgroundColor: 'var(--tab-pill-background-color)',
		// @ts-expect-error -- cssMap does not support runtime CSS variables for colors.
		color: 'var(--tab-pill-text-color)',
		// @ts-expect-error -- cssMap does not support runtime CSS variables for colors.
		borderColor: 'var(--tab-pill-border-color)',
		'&:hover': {
			// @ts-expect-error -- cssMap does not support runtime CSS variables for colors.
			backgroundColor: 'var(--tab-pill-background-color-hovered)',
		},
	},
});

export const TabPillColorSurface: React.ForwardRefExoticComponent<
	TabPillColorSurfaceProps & React.RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, TabPillColorSurfaceProps>(
	(
		{
			children,
			color,
			id,
			isActive,
			isDeletePreviewed,
			isEditing,
			onClick,
			onKeyDown,
			orientation = 'horizontal',
			showMenu,
			tabIndex,
		},
		ref,
	) => {
		const appearanceMode = isStandardTabColorPaletteEnabled() ? 'standard' : 'legacy';
		const tabPillColor = color ? getTabColorAppearance(color, appearanceMode).tabPill : undefined;

		return (
			<Box
				as="span"
				id={id}
				testId={`native-tabs-pill-${id}`}
				ref={isEditing ? undefined : ref}
				role={isEditing ? undefined : 'tab'}
				aria-selected={isEditing ? undefined : isActive}
				aria-controls={isEditing ? undefined : `panel-${id}`}
				tabIndex={isEditing ? undefined : tabIndex}
				onClick={isEditing ? undefined : onClick}
				onKeyDown={isEditing ? undefined : onKeyDown}
				style={
					{
						'--tab-pill-background-color': tabPillColor
							? isActive
								? tabPillColor.active.backgroundColor
								: tabPillColor.inactive.backgroundColor
							: undefined,
						'--tab-pill-background-color-hovered': tabPillColor?.inactive.backgroundColorHovered,
						'--tab-pill-border-color': tabPillColor
							? isActive
								? tabPillColor.active.borderColor
								: tabPillColor.inactive.borderColor
							: undefined,
						'--tab-pill-text-color': tabPillColor
							? isActive
								? tabPillColor.active.textColor
								: tabPillColor.inactive.textColor
							: undefined,
					} as TabPillColorCustomProperties
				}
				xcss={cx(
					styles.root,
					!tabPillColor && isActive && styles.defaultActive,
					!tabPillColor && !isActive && styles.defaultInactive,
					tabPillColor && isActive && styles.coloredActive,
					tabPillColor && !isActive && styles.coloredInactive,
					isDeletePreviewed && styles.deletePreview,
					isActive && showMenu && styles.activeWithMenu,
					orientation === 'vertical' && styles.vertical,
					orientation === 'vertical' && styles.verticalRadiusStart,
					orientation === 'vertical' && !(isActive && showMenu) && styles.verticalRadiusEnd,
				)}
			>
				{children}
			</Box>
		);
	},
);

TabPillColorSurface.displayName = 'TabPillColorSurface';
